import { useEffect, useMemo, useState } from 'react';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { Label } from '~/components/ui/label';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '~/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { useCoursesQuery } from '~/pages/SchoolYearView/useCoursesQuery';
import { useAssignInstructor } from '~/pages/Courses/useRosterMutations';

type Role = 'PRIMARY' | 'ASSISTANT' | 'AIDE';

interface SchoolYearLike {
  id: string;
  slug: string;
  name: string;
  startDate: unknown;
  endDate: unknown;
}

interface AlreadyAssigned {
  classOfferingId: string;
  role: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  staffPersonId: string;
  staffDisplayName: string;
  schoolYears: ReadonlyArray<SchoolYearLike>;
  alreadyAssigned: ReadonlyArray<AlreadyAssigned>;
  onComplete: () => void;
}

function pickDefaultYear(years: ReadonlyArray<SchoolYearLike>): string | undefined {
  if (years.length === 0) return undefined;
  const today = new Date().toISOString().slice(0, 10);
  const current = years.find((y) => {
    const start = String(y.startDate ?? '');
    const end = String(y.endDate ?? '');
    return start && end && start <= today && today <= end;
  });
  if (current) return current.id;
  // Fallback: latest by start date
  const sorted = [...years].sort((a, b) => {
    const aStart = String(a.startDate ?? '');
    const bStart = String(b.startDate ?? '');
    return aStart < bStart ? 1 : aStart > bStart ? -1 : 0;
  });
  return sorted[0]?.id;
}

export default function AssignToClassesDialog({
  open,
  onOpenChange,
  staffPersonId,
  staffDisplayName,
  schoolYears,
  alreadyAssigned,
  onComplete,
}: Props) {
  const [schoolYearId, setSchoolYearId] = useState<string | undefined>(pickDefaultYear(schoolYears));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [role, setRole] = useState<Role>('PRIMARY');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setSchoolYearId(pickDefaultYear(schoolYears));
      setSelected(new Set());
      setRole('PRIMARY');
      setErrors([]);
    }
  }, [open, schoolYears]);

  const { data, loading } = useCoursesQuery(schoolYearId);
  const [assignInstructor] = useAssignInstructor();

  const assignedIds = useMemo(
    () => new Set(alreadyAssigned.map((a) => a.classOfferingId)),
    [alreadyAssigned]
  );

  const courses = data?.schoolYear?.courses ?? [];

  const toggleOne = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllInCourse = (offeringIds: string[]) => {
    const availableIds = offeringIds.filter((id) => !assignedIds.has(id));
    const allSelected = availableIds.every((id) => selected.has(id));
    setSelected((s) => {
      const next = new Set(s);
      availableIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors([]);
    const collected: string[] = [];
    try {
      for (const offeringId of selected) {
        const res = await assignInstructor({
          variables: { input: { classOfferingId: offeringId, personId: staffPersonId, role } },
        });
        const userErrors = res.data?.assignInstructor?.userErrors;
        if (userErrors?.length) {
          collected.push(`${offeringId}: ${userErrors.map((e: any) => e.message).join(', ')}`);
        }
      }
      if (collected.length > 0) {
        setErrors(collected);
      } else {
        onComplete();
      }
    } catch (e: any) {
      setErrors([e.message || 'Save failed']);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assign {staffDisplayName} to classes</DialogTitle>
          <DialogDescription>
            Pick a school year and check the class sections to assign. Classes they're already an instructor for are shown grayed out.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="schoolYear">School year</Label>
              <Select value={schoolYearId ?? ''} onValueChange={setSchoolYearId}>
                <SelectTrigger id="schoolYear"><SelectValue placeholder="Pick a year" /></SelectTrigger>
                <SelectContent>
                  {schoolYears.map((y) => (
                    <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="role">Role (applied to all)</Label>
              <Select value={role} onValueChange={(v) => setRole(v as Role)}>
                <SelectTrigger id="role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PRIMARY">Primary</SelectItem>
                  <SelectItem value="ASSISTANT">Assistant</SelectItem>
                  <SelectItem value="AIDE">Aide</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading && courses.length === 0 && (
            <p className="text-sm text-muted-foreground">Loading courses…</p>
          )}

          {!loading && courses.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No courses yet in this school year. Create a course first from the Courses page.
            </p>
          )}

          <div className="space-y-3">
            {courses.map((course) => {
              const offerings = course.classOfferings ?? [];
              if (offerings.length === 0) return null;
              const availableIds = offerings.filter((o) => !assignedIds.has(o.id)).map((o) => o.id);
              const allChecked = availableIds.length > 0 && availableIds.every((id) => selected.has(id));

              return (
                <div key={course.id} className="rounded-md border p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{course.name}</div>
                      {course.catalogCode && (
                        <div className="text-xs text-muted-foreground">{course.catalogCode}</div>
                      )}
                    </div>
                    {availableIds.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleAllInCourse(offerings.map((o) => o.id))}
                      >
                        {allChecked ? 'Clear all' : 'Select all'}
                      </Button>
                    )}
                  </div>
                  <div className="mt-2 space-y-1">
                    {offerings.map((o) => {
                      const already = assignedIds.has(o.id);
                      return (
                        <label
                          key={o.id}
                          className={`flex items-center gap-2 text-sm rounded px-2 py-1 ${
                            already ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'
                          }`}
                        >
                          <Checkbox
                            checked={selected.has(o.id)}
                            disabled={already}
                            onCheckedChange={() => toggleOne(o.id)}
                          />
                          <span className="flex-1">
                            {o.displayName || `Section ${o.section}`}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {o.term?.id ? offeringTermName(o, course) : ''}
                          </Badge>
                          {already && <Badge variant="secondary" className="text-xs">Assigned</Badge>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {errors.length > 0 && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 space-y-1">
              {errors.map((e, i) => (
                <p key={i} className="text-xs text-destructive">{e}</p>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || selected.size === 0}>
            {saving ? 'Assigning…' : `Assign to ${selected.size} class${selected.size === 1 ? '' : 'es'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function offeringTermName(offering: any, course: any): string {
  const term = (course.terms ?? []).find((t: any) => t.id === offering.term?.id);
  return term?.name ?? '';
}
